import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import anime from "animejs";
import { RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { usePlaySound } from "common/utils";
import { CreatorGameCaseManager } from "common/elements";
import { IconViewed } from "features/user/assets";
import { selectAllProjects } from "features/creator/slice";
import { projectAnimation } from "features/creator/project/util";
import { ProjectType, ProjectEntity } from "features/creator/types";
import {
  CardBg,
  IconProjectCard,
  IconPublish,
  IconPublishCopiedOn,
  IconPublishCopiedOff,
  IconProjectCardFavorited,
  IconProjectCardFavoritedReverse,
} from "features/creator/assets";

const ProjectGridCard = ({
  project,
  isSelected,
  onSelect,
}: {
  project: ProjectEntity;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const play = usePlaySound();

  const handleClick = () => {
    if (isSelected) return;
    play();
    onSelect();
  };

  return (
    <div
      id={`user-app-project-container-${project.project_id}`}
      onClick={handleClick}
      className="flex-col-el flex-center w-[168px] cursor-pointer pointer-events-auto"
    >
      <div className="flex-col-center">
        <div className="flex-col-center w-[48px] h-[40px]">
          {project.published ? (
            <div className="flex-col-center w-[40px] h-[40px]">
              {project?.enable_copied ? <IconPublishCopiedOn /> : <IconPublishCopiedOff />}
            </div>
          ) : (
            <img
              alt="publish status"
              src={`${Constants.assetHost}/assets/images/publish_on.png`}
              className="w-[36px] h-[36px]"
            />
          )}
        </div>

        <div className="flex-col-center rounded-2xl p-2">
          <div className="flex-col-el flex-center w-[144px] h-[156px] absolute -z-0">
            <CardBg />
          </div>

          <div className="flex-col-center w-[120px] h-[148px] ml-3">
            <div className="flex-col-el flex-center absolute w-[62px] h-[64px]">
              <img
                alt="switch"
                className="w-full h-full"
                src={`${Constants.assetHost}/assets/images/project_card_switch.png`}
              />
            </div>

            <div className={`flex-col-center w-full h-full app-project-card-${project.project_id}`}>
              <img
                alt="app card"
                src={`${Constants.assetHost}/assets/images/app_card_user.png`}
                className="w-full h-full"
              />

              <div className="flex-col-el flex-center absolute top-8 w-full px-2 h-[100px]">
                {project?.cover || project?.capture ? (
                  <div className="flex-col-center w-full h-full overflow-hidden bg-white relative">
                    <img
                      alt="アプリのキャプチャー"
                      className="w-full h-full object-cover"
                      src={project?.cover ?? project?.capture}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          `${Constants.assetHost}/assets/images/default_capture.png`;
                      }}
                    />
                    <div className="flex-row-center absolute bottom-0 left-0 right-0 gap-2 bg-white/80 py-1">
                      <div className="flex-row-center">
                        <div className="flex-col-center w-[24px] h-[24px]">
                          <IconViewed className="w-full h-full" />
                        </div>
                        <p className="type-text type-sm text-brown-100 leading-none">
                          {project.viewed}
                        </p>
                      </div>
                      <div className="flex-row-center">
                        <div className="flex-col-center w-[24px] h-[24px]">
                          {project.is_favorited ? (
                            <IconProjectCardFavorited />
                          ) : (
                            <IconProjectCardFavoritedReverse />
                          )}
                        </div>
                        <p className="type-text type-sm text-red-100 leading-none">
                          {project.favorited}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex-col-el justify-end w-full h-full bg-white">
                    <div className="flex-row-center gap-2 bg-white/80 py-1">
                      <div className="flex-row-center">
                        <div className="flex-col-center w-[24px] h-[24px]">
                          <IconViewed className="w-full h-full" />
                        </div>
                        <p className="type-text type-sm text-brown-100 leading-none">
                          {project.viewed}
                        </p>
                      </div>
                      <div className="flex-row-center">
                        <div className="flex-col-center w-[24px] h-[24px]">
                          {project.is_favorited ? (
                            <IconProjectCardFavorited />
                          ) : (
                            <IconProjectCardFavoritedReverse />
                          )}
                        </div>
                        <p className="type-text type-sm text-red-100 leading-none">
                          {project.favorited}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-row-center w-full">
        <p className="type-text type-sm text-center text-body-ellipsis w-full leading-tight text-textcolor-black">
          {project.name}
        </p>
      </div>
    </div>
  );
};

const ProjectGridPlaceholder = () => (
  <div className="flex-col-el flex-center w-[168px]">
    <div className="flex-col-center">
      <div className="flex-col-center w-[48px] h-[40px]" />
      <div className="flex-col-center rounded-2xl p-2">
        <div className="flex-col-el flex-center w-[144px] h-[156px]">
          <CardBg />
        </div>
      </div>
    </div>
    <div className="flex-row-center w-full h-[24px]" />
  </div>
);

const ProjectPreview = ({ project }: { project: ProjectEntity | null }) => {
  const config = useSelector((state: RootState) => state.config.userConfig);
  const caseImage = CreatorGameCaseManager[config.game_case];

  return (
    <div
      className="flex-col-el relative w-[200px] h-[150px] shrink-0"
      style={{
        filter: "drop-shadow(0px 4px 8px rgba(63, 74, 97, 0.18))",
      }}
    >
      <img
        alt="game case"
        src={`${Constants.assetHost}/assets/images/${caseImage}`}
        className="absolute inset-0 w-full h-full object-fill"
      />
      <div className="flex-col-el absolute top-[11px] left-[13px] w-[170px] h-[125px] overflow-hidden bg-white rounded-md">
        {project?.cover || project?.capture ? (
          <img
            alt="アプリのキャプチャー"
            className="w-full h-full object-cover"
            src={project?.cover ?? project?.capture}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                `${Constants.assetHost}/assets/images/default_capture.png`;
            }}
          />
        ) : (
          <div className="flex-col-el flex-center w-full h-full bg-white" />
        )}
      </div>
    </div>
  );
};

const StatBlock = ({
  icon,
  value,
  color,
}: {
  icon: React.ReactNode;
  value: number;
  color: string;
}) => (
  <div className="flex-row-center gap-2">
    <div className="flex-col-center w-[36px] h-[36px]">{icon}</div>
    <p className="type-text type-title leading-none" style={{ color }}>
      {value}
    </p>
  </div>
);

const ProjectInfoRow = ({
  title,
  icon,
  content,
}: {
  title: string;
  icon: React.ReactNode;
  content: string;
}) => (
  <div className="flex-row-center gap-2">
    <div className="flex-col-el w-[72px] shrink-0">
      <p
        className="type-text type-sm text-textcolor-black text-right leading-tight"
        style={{ whiteSpace: "pre" }}
      >
        {title}
      </p>
    </div>
    <div className="flex-col-center w-[32px] h-[32px] shrink-0">{icon}</div>
    <div className="flex-col-el w-[140px] shrink-0">
      <p className="type-text type-sm text-textcolor-black text-left leading-tight select-text">
        {content}
      </p>
    </div>
  </div>
);

const EmptyCassetteIllustration = ({ variant }: { variant: "pencil" | "spark" | "puzzle" }) => {
  if (variant === "pencil") {
    return (
      <svg viewBox="0 0 100 100" className="w-full h-full" fill="none">
        <circle cx="50" cy="50" r="32" fill="#FFF4D6" />
        <g transform="rotate(-30 50 50)">
          <rect x="34" y="44" width="36" height="12" rx="2" fill="#F9C74F" />
          <rect x="34" y="44" width="6" height="12" fill="#FC6560" />
          <polygon points="70,44 80,50 70,56" fill="#FFE4B0" />
          <polygon points="74,48 80,50 74,52" fill="#3F4A61" />
        </g>
        <path d="M22 30 L25 36 L31 38 L25 40 L22 46 L19 40 L13 38 L19 36 Z" fill="#F9C74F" />
        <circle cx="78" cy="74" r="3" fill="#FC6560" opacity="0.7" />
        <circle cx="26" cy="72" r="2.5" fill="#80EAC1" opacity="0.8" />
      </svg>
    );
  }
  if (variant === "spark") {
    return (
      <svg viewBox="0 0 100 100" className="w-full h-full" fill="none">
        <circle cx="50" cy="50" r="34" fill="#E0F7EA" />
        <path
          d="M50 22 L55 44 L77 50 L55 56 L50 78 L45 56 L23 50 L45 44 Z"
          fill="#F9C74F"
          stroke="#E5A823"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <circle cx="28" cy="30" r="3" fill="#FC6560" />
        <circle cx="74" cy="30" r="2.5" fill="#4C9DF1" />
        <circle cx="76" cy="74" r="3" fill="#80EAC1" />
        <circle cx="24" cy="72" r="2" fill="#F9C74F" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full" fill="none">
      <circle cx="50" cy="50" r="34" fill="#FFE9E8" />
      <path
        d="M30 38 h14 a6 6 0 1 1 12 0 h14 v14 a6 6 0 1 0 0 12 v14 h-14 a6 6 0 1 0 -12 0 h-14 v-14 a6 6 0 1 1 0 -12 z"
        fill="#80C7F4"
        stroke="#3F8FD0"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="44" cy="48" r="2.5" fill="#fff" />
      <circle cx="56" cy="48" r="2.5" fill="#fff" />
      <path
        d="M42 60 Q50 66 58 60"
        stroke="#3F8FD0"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
      <circle cx="22" cy="26" r="2" fill="#F9C74F" />
      <circle cx="80" cy="78" r="2.5" fill="#FC6560" opacity="0.7" />
    </svg>
  );
};

const EmptyCassette = ({
  scale = 1,
  variant,
}: {
  scale?: number;
  variant: "pencil" | "spark" | "puzzle";
}) => (
  <div
    className="flex-col-el relative shrink-0"
    style={{ width: 120 * scale, height: 152 * scale }}
  >
    <img
      alt="app card"
      src={`${Constants.assetHost}/assets/images/app_card_user.png`}
      className="absolute inset-0 w-full h-full object-fill"
    />
    <div
      className="flex-col-el flex-center absolute rounded bg-white"
      style={{
        top: 32 * scale,
        bottom: 18 * scale,
        left: 8 * scale,
        right: 8 * scale,
      }}
    >
      <EmptyCassetteIllustration variant={variant} />
    </div>
  </div>
);

const SparkleDecoration = ({
  top,
  left,
  size,
  color,
  variant,
}: {
  top: number;
  left: number;
  size: number;
  color: string;
  variant: "dot" | "star";
}) => (
  <div className="flex-col-el absolute z-[5]" style={{ top, left, width: size, height: size }}>
    {variant === "dot" ? (
      <div
        className="w-full h-full rounded-full"
        style={{ backgroundColor: color, opacity: 0.55 }}
      />
    ) : (
      <svg viewBox="0 0 24 24" fill={color} className="w-full h-full">
        <path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" />
      </svg>
    )}
  </div>
);

export const UserProgressCreatorProject = ({
  selected,
  close,
}: {
  selected: boolean;
  close: () => void;
}) => {
  const play = usePlaySound();
  const navigate = useNavigate();
  const projects = useSelector(selectAllProjects);
  const active = useSelector((state: RootState) => state.user.active);
  const [centerIndex, setCenterIndex] = useState(0);
  const [isFirst, setIsFirst] = useState(true);

  const userProjects = useMemo(
    () =>
      projects.filter(
        (project) =>
          project.author_uid === active.uid &&
          !project.deleted_at &&
          project.type === ProjectType.BASIC,
      ),
    [projects, active.uid],
  );

  useEffect(() => {
    setCenterIndex(0);
    setIsFirst(true);
  }, [userProjects]);

  useEffect(() => {
    if (centerIndex >= userProjects.length && userProjects.length > 0) {
      setCenterIndex(0);
    }
  }, [userProjects.length, centerIndex]);

  useEffect(() => {
    if (!selected || !isFirst || userProjects.length === 0) return;
    const targetId = userProjects[centerIndex]?.project_id;
    if (!targetId) return;
    anime({
      targets: document.getElementsByClassName(`app-project-card-${targetId}`),
      opacity: [1, 0],
      duration: 1,
      easing: "easeInOutQuad",
    });
    setIsFirst(false);
  }, [selected, isFirst, userProjects, centerIndex]);

  const selectedProject = userProjects[centerIndex] ?? null;

  const handleGoToCreator = () => {
    play();
    close();
    navigate("/creator");
  };

  const handleSelect = (index: number) => () => {
    if (index === centerIndex) return;
    const previousId = userProjects[centerIndex]?.project_id;
    const nextId = userProjects[index]?.project_id;
    if (!previousId || !nextId) {
      setCenterIndex(index);
      return;
    }
    projectAnimation(previousId, nextId, () => {
      setCenterIndex(index);
    });
  };

  if (userProjects.length === 0) {
    return (
      <div
        className="flex-col-el flex-center w-full h-full overflow-hidden p-6"
        style={{ display: selected ? undefined : "none" }}
      >
        <div className="flex-col-el items-center gap-6">
          <div className="flex-col-el relative items-center w-[700px] h-[320px]">
            <div className="flex-col-el absolute top-[20px] left-1/2 -translate-x-1/2 w-[480px] h-[230px] rounded-t-full bg-green-60/15 z-0" />

            <SparkleDecoration top={40} left={170} size={14} color="#F9E532" variant="star" />
            <SparkleDecoration top={130} left={130} size={12} color="#FC6560" variant="dot" />
            <SparkleDecoration top={70} left={560} size={12} color="#80EAC1" variant="dot" />
            <SparkleDecoration top={170} left={590} size={14} color="#F9E532" variant="star" />
            <SparkleDecoration top={100} left={350} size={16} color="#80EAC1" variant="star" />
            <SparkleDecoration top={30} left={520} size={10} color="#4C9DF1" variant="dot" />
            <SparkleDecoration top={190} left={160} size={10} color="#4C9DF1" variant="dot" />

            <div
              className="flex-col-el absolute left-1/2 -translate-x-1/2 bottom-[16px] w-[640px] h-[44px] rounded-xl z-0"
              style={{
                background:
                  "linear-gradient(180deg, #F2D6A8 0%, #E5BC85 45%, #C99863 80%, #A6764C 100%)",
                boxShadow:
                  "inset 0 5px 0 rgba(255,255,255,0.5), inset 0 -4px 0 rgba(120,80,50,0.4), 0 6px 14px rgba(63,74,97,0.18)",
              }}
            />

            <div className="flex-row-el absolute left-1/2 -translate-x-1/2 bottom-[30px] items-end justify-center gap-4 z-10">
              <img
                alt="pencil holder"
                src={`${Constants.assetHost}/assets/images/pencil-holder.png`}
                className="w-[100px] h-[140px] object-contain shrink-0"
              />
              <EmptyCassette scale={0.85} variant="pencil" />
              <EmptyCassette scale={1} variant="spark" />
              <EmptyCassette scale={0.85} variant="puzzle" />
              <div className="flex-row-el relative items-end shrink-0 w-[120px] h-[90px]">
                <img
                  alt="puzzle piece"
                  src={`${Constants.assetHost}/assets/images/puzzle-piece.png`}
                  className="absolute left-0 bottom-0 w-[55px] h-[55px] object-contain z-20"
                />
                <img
                  alt="triangle ruler"
                  src={`${Constants.assetHost}/assets/images/triangle-ruler.png`}
                  className="absolute right-0 bottom-0 w-[85px] h-[85px] object-contain z-10"
                />
              </div>
            </div>
          </div>

          <div className="flex-col-el items-center gap-2">
            <p className="type-text type-title text-textcolor-black text-center">
              {I18n.t("MSG_USER_PROFILE_PROGRESS_CREATOR_PROJECT_EMPTY")}
            </p>
            <p className="type-text type-body text-textcolor-black/70 text-center">
              {I18n.t("MSG_USER_PROFILE_PROGRESS_CREATOR_PROJECT_EMPTY_DESC")}
            </p>
          </div>

          <div className="flex-row-el flex-center upsilon">
            <button className="btn btn-primary type-inherit-font" onClick={handleGoToCreator}>
              <div className="flex-row-el flex-center relative">
                <p className="type-text type-body text-white">
                  {I18n.t("MSG_USER_PROFILE_PROGRESS_CREATOR_GOTO_BTN")}
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex-row-el w-full h-full p-3 gap-4 overflow-hidden items-start"
      style={{ display: selected ? undefined : "none" }}
    >
      <div
        className="flex-col-el flex-1 min-w-0 h-full rounded-2xl p-4 justify-center"
        style={{
          background:
            "linear-gradient(180deg, rgba(248,244,232,0.6) 0%, rgba(248,244,232,0.2) 100%)",
          boxShadow: "inset 0 2px 4px rgba(63,74,97,0.06)",
        }}
      >
        <div className="grid grid-cols-5 gap-3 w-full justify-items-center">
          {Array(10)
            .fill(0)
            .map((_, index) =>
              userProjects[index] ? (
                <ProjectGridCard
                  key={userProjects[index].project_id}
                  project={userProjects[index]}
                  isSelected={index === centerIndex}
                  onSelect={handleSelect(index)}
                />
              ) : (
                <ProjectGridPlaceholder key={index} />
              ),
            )}
        </div>
      </div>

      <div
        className="flex-col-el items-center w-[300px] shrink-0 gap-3 justify-center h-full rounded-2xl p-4"
        style={{
          background:
            "linear-gradient(180deg, rgba(248,244,232,0.6) 0%, rgba(248,244,232,0.2) 100%)",
          boxShadow: "inset 0 2px 4px rgba(63,74,97,0.06)",
        }}
      >
        <ProjectPreview project={selectedProject} />

        <div className="flex-col-el items-center w-full px-2">
          <p className="type-text type-title text-textcolor-black text-center text-body-ellipsis w-full leading-tight">
            {selectedProject?.name}
          </p>
        </div>

        <div className="flex-row-center gap-6 w-full">
          <StatBlock
            icon={<IconViewed className="w-full h-full" />}
            value={selectedProject?.viewed ?? 0}
            color="#A54D21"
          />
          <StatBlock
            icon={<IconProjectCardFavorited className="w-full h-full" />}
            value={selectedProject?.favorited ?? 0}
            color="#FC6560"
          />
        </div>

        {selectedProject && (
          <div className="flex-col-el items-center gap-2 w-full">
            <ProjectInfoRow
              title={I18n.t("MSG_CREATOR_APP_DETAIL_TITLE_APP_CODE")}
              icon={<IconProjectCard className="w-full h-full" />}
              content={selectedProject.inquiry_id ?? ""}
            />
            <ProjectInfoRow
              title={I18n.t("MSG_CREATOR_APP_DETAIL_TITLE_SHARED_APP")}
              icon={
                selectedProject.published ? (
                  <IconPublish fill="#4C9DF1" stroke="#3F4A61" className="w-full h-full" />
                ) : (
                  <img
                    alt="publish"
                    src={`${Constants.assetHost}/assets/images/publish_on.png`}
                    className="w-full h-full"
                  />
                )
              }
              content={
                selectedProject.published
                  ? I18n.t("MSG_CREATOR_APP_DETAIL_TITLE_SHARED_APP_PUBLISHED")
                  : I18n.t("MSG_CREATOR_APP_DETAIL_TITLE_SHARED_APP_NOT_PUBLISHED")
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};
